/**
 * Conduct modules data access (migration 0007): code-of-ethics attestations +
 * political-contributions (pay-to-play) tracking. Client-side under RLS.
 */
import { supabase } from "@/lib/supabase/client";

/* ----------------------------------------------------- Attestations */

export type AttestationType =
  | "code_of_ethics"
  | "holdings_report"
  | "political_contribution"
  | "custom";
export type CampaignStatus = "draft" | "open" | "closed";
export type AttestationStatus = "pending" | "acknowledged" | "exception";

export interface CampaignRow {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  attestation_type: AttestationType;
  compliance_task_id: string | null;
  due_date: string | null;
  status: CampaignStatus;
  created_at: string;
}

export interface AttestationRow {
  id: string;
  tenant_id: string;
  campaign_id: string;
  person_name: string;
  person_email: string | null;
  person_role: string | null;
  status: AttestationStatus;
  acknowledged_at: string | null;
  signature_name: string | null;
  notes: string | null;
}

const CAMPAIGN_COLS =
  "id, tenant_id, title, description, attestation_type, compliance_task_id, due_date, status, created_at";
const ATTESTATION_COLS =
  "id, tenant_id, campaign_id, person_name, person_email, person_role, status, acknowledged_at, signature_name, notes";

export async function listCampaigns(tenantId: string): Promise<CampaignRow[]> {
  const { data, error } = await supabase
    .from("attestation_campaigns")
    .select(CAMPAIGN_COLS)
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(`listCampaigns: ${error.message}`);
  return data ?? [];
}

export async function createCampaign(input: {
  tenant_id: string;
  title: string;
  description?: string | null;
  attestation_type: AttestationType;
  due_date?: string | null;
}): Promise<CampaignRow> {
  const { data, error } = await supabase
    .from("attestation_campaigns")
    .insert(input)
    .select(CAMPAIGN_COLS)
    .single();
  if (error) throw new Error(`createCampaign: ${error.message}`);
  return data;
}

export async function updateCampaign(
  id: string,
  patch: { status?: CampaignStatus; title?: string; due_date?: string | null },
): Promise<void> {
  const { error } = await supabase.from("attestation_campaigns").update(patch).eq("id", id);
  if (error) throw new Error(`updateCampaign: ${error.message}`);
}

export async function deleteCampaign(id: string): Promise<void> {
  const { error } = await supabase.from("attestation_campaigns").delete().eq("id", id);
  if (error) throw new Error(`deleteCampaign: ${error.message}`);
}

export async function listAttestations(campaignId: string): Promise<AttestationRow[]> {
  const { data, error } = await supabase
    .from("attestations")
    .select(ATTESTATION_COLS)
    .eq("campaign_id", campaignId)
    .order("person_name", { ascending: true });
  if (error) throw new Error(`listAttestations: ${error.message}`);
  return data ?? [];
}

export async function addPerson(
  tenantId: string,
  campaignId: string,
  person: { person_name: string; person_email?: string | null; person_role?: string | null },
): Promise<void> {
  const { error } = await supabase
    .from("attestations")
    .insert({ tenant_id: tenantId, campaign_id: campaignId, ...person });
  if (error) throw new Error(`addPerson: ${error.message}`);
}

export async function setAttestationStatus(
  row: AttestationRow,
  status: AttestationStatus,
  signatureName?: string,
): Promise<void> {
  const patch: Record<string, unknown> = {
    status,
    acknowledged_at: status === "acknowledged" ? new Date().toISOString() : null,
    signature_name: status === "acknowledged" ? (signatureName ?? row.person_name) : null,
  };
  const { error } = await supabase.from("attestations").update(patch).eq("id", row.id);
  if (error) throw new Error(`setAttestationStatus: ${error.message}`);
}

/* --------------------------------------------- Political contributions */

export type ContributionStatus = "logged" | "under_review" | "cleared" | "flagged" | "remediated";

export interface ContributionRow {
  id: string;
  tenant_id: string;
  contributor_name: string;
  contributor_role: string | null;
  recipient_name: string;
  office_sought: string | null;
  government_entity: string | null;
  jurisdiction: string | null;
  party: string | null;
  amount_usd: number;
  contribution_date: string;
  can_vote_for_recipient: boolean;
  pre_cleared: boolean;
  pre_cleared_at: string | null;
  status: ContributionStatus;
  review_notes: string | null;
}

const CONTRIB_COLS =
  "id, tenant_id, contributor_name, contributor_role, recipient_name, office_sought, government_entity, jurisdiction, party, amount_usd, contribution_date, can_vote_for_recipient, pre_cleared, pre_cleared_at, status, review_notes";

export async function listContributions(tenantId: string): Promise<ContributionRow[]> {
  const { data, error } = await supabase
    .from("political_contributions")
    .select(CONTRIB_COLS)
    .eq("tenant_id", tenantId)
    .order("contribution_date", { ascending: false });
  if (error) throw new Error(`listContributions: ${error.message}`);
  return (data ?? []).map((r) => ({ ...r, amount_usd: Number(r.amount_usd) }));
}

export interface CreateContributionInput {
  tenant_id: string;
  contributor_name: string;
  contributor_role?: string | null;
  recipient_name: string;
  office_sought?: string | null;
  government_entity?: string | null;
  jurisdiction?: string | null;
  amount_usd: number;
  contribution_date: string;
  can_vote_for_recipient: boolean;
  pre_cleared?: boolean;
}

export async function createContribution(input: CreateContributionInput): Promise<void> {
  const { error } = await supabase.from("political_contributions").insert(input);
  if (error) throw new Error(`createContribution: ${error.message}`);
}

export async function setContributionStatus(
  id: string,
  status: ContributionStatus,
  reviewNotes?: string,
): Promise<void> {
  const patch: Record<string, unknown> = { status };
  if (reviewNotes !== undefined) patch.review_notes = reviewNotes || null;
  const { error } = await supabase.from("political_contributions").update(patch).eq("id", id);
  if (error) throw new Error(`setContributionStatus: ${error.message}`);
}

export async function deleteContribution(id: string): Promise<void> {
  const { error } = await supabase.from("political_contributions").delete().eq("id", id);
  if (error) throw new Error(`deleteContribution: ${error.message}`);
}
